import i18next, { type i18n, type InitOptions, type Resource } from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import es from './locales/es.json';
import type { WebNamespace } from './web';

export const LOCALES = ['en', 'es'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'es';
export const FALLBACK_LOCALE: Locale = 'en';

export const NAMESPACES = ['common', 'tv', 'mobile', 'cms'] as const;
export type Namespace = (typeof NAMESPACES)[number];
export const DEFAULT_NS: Namespace = 'common';

/** Resource bundle per language, one object per namespace. */
export const i18nResources = { en, es } as const satisfies Record<
  Locale,
  Record<Namespace, object>
>;
export type I18nResources = (typeof i18nResources)['en'];

/** Typed keys for `t()` / `useTranslation()` (react-i18next v15+ typing). */
declare module 'i18next' {
  interface CustomTypeOptions {
    defaultNS: 'common';
    resources: I18nResources & { web: WebNamespace };
    returnNull: false;
  }
}

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/**
 * Picks the product locale from a BCP-47 tag (`navigator.language`) or a stored
 * override. Spanish variants → `es`; anything else → `en`; unknown → default.
 */
export function detectLocale(
  tag: string | null | undefined,
  fallback: Locale = DEFAULT_LOCALE,
): Locale {
  if (!tag) return fallback;
  const base = tag.toLowerCase().split(/[-_]/)[0];
  return isLocale(base) ? base : base ? 'en' : fallback;
}

export interface InitI18nOptions {
  /** Initial language; defaults to {@link DEFAULT_LOCALE}. */
  lng?: Locale | string;
  /** Override or extend the bundled resources (tests, previews). */
  resources?: Resource;
  fallbackLng?: Locale;
  /** Use an existing instance instead of creating one (default: new instance). */
  instance?: i18n;
  /** Wire `initReactI18next` (default true). Set false for non-React code (Workers, tests). */
  react?: boolean;
  /** Extra i18next options merged last. */
  options?: Partial<InitOptions>;
}

/**
 * Creates and initializes an i18next instance with the bundled en/es resources.
 * Resources are inline, so `init` resolves synchronously (no backend), which
 * keeps the TV boot sequence (docs/04 §1) free of extra network round-trips.
 */
export async function initI18n(opts: InitI18nOptions = {}): Promise<i18n> {
  const instance = opts.instance ?? i18next.createInstance();
  if (opts.react !== false) instance.use(initReactI18next);
  await instance.init({
    lng: opts.lng ?? DEFAULT_LOCALE,
    fallbackLng: opts.fallbackLng ?? FALLBACK_LOCALE,
    supportedLngs: [...LOCALES],
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    ns: [...NAMESPACES],
    defaultNS: DEFAULT_NS,
    fallbackNS: DEFAULT_NS,
    resources: opts.resources ?? i18nResources,
    interpolation: { escapeValue: false },
    returnNull: false,
    ...opts.options,
  });
  return instance;
}

/** Namespace-prefixed key, e.g. `nsKey('tv', 'pairing.title')` → `'tv:pairing.title'`. */
export function nsKey<N extends Namespace>(ns: N, key: string): `${N}:${string}` {
  return `${ns}:${key}`;
}

export { diffLocaleKeys, flattenKeys } from './parity';
